import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { User, Handshake, Folder, Pin, Clock, Check, Timer, Phone, RefreshCw, FileText, Users, Wallet, HelpCircle } from 'lucide-react';
import {
  Reminder,
  formatReminderDate,
  ReminderType,
  ReminderStatus,
  handleApiError
} from '../../services/reminderService';

import reminderApi from '../../services/reminderService';
import AssigneesControl from '../common/AssigneesControl';
import PostponeReminderModal from './PostponeReminderModal';

// Local function to get Lucide icon for reminder type
const getReminderTypeIconComponent = (type: ReminderType): React.ReactElement => {
  switch (type) {
    case ReminderType.Call: return <Phone size={16} />;
    case ReminderType.FollowUp: return <RefreshCw size={16} />;
    case ReminderType.Document: return <FileText size={16} />;
    case ReminderType.Meeting: return <Users size={16} />;
    case ReminderType.Payment: return <Wallet size={16} />;
    case ReminderType.Custom: return <Pin size={16} />;
    default: return <HelpCircle size={16} />;
  }
};

const Card = styled.div<{ isOverdue?: boolean }>`
  background: transparent;
  padding: 14px 12px;
  margin-bottom: 8px;
  border-right: 3px solid #94a3b8;
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #fafbfc;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TypeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
`;

const Description = styled.p`
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 6px 0;
  line-height: 1.4;
  white-space: pre-line;
`;

const FailureBlock = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  background: #FEF2F2;
  border: 1px solid #DC2626;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  color: #991B1B;
  font-weight: 500;
  line-height: 1.4;
  white-space: pre-line;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: var(--text-tertiary);
`;

const AssigneeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const DateTime = styled.span<{ isOverdue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.isOverdue ? 'var(--error-color)' : 'inherit'};
  font-weight: ${props => props.isOverdue ? '500' : 'normal'};
`;

const EntityInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EntityLink = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-hover);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-light);
          color: var(--error-color);
          &:hover { background: var(--error-lighter); }
        `;
      default:
        return `
          background: var(--background-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { 
            background: var(--hover-background);
            border-color: var(--primary-color);
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextButton = styled.button<{ $color: string; $outlined?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.$outlined ? 'transparent' : 'none'};
  border: ${props => props.$outlined ? `1px solid ${props.$color}30` : 'none'};
  padding: ${props => props.$outlined ? '4px 10px' : '4px 6px'};
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$color};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 5px;

  &:hover {
    background: ${props => `${props.$color}10`};
    border-color: ${props => props.$outlined ? props.$color : 'transparent'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HandledNotesInput = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  resize: vertical;
  min-height: 60px;
  margin-top: 8px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const StatusBlock = styled.div<{ $variant: 'handled' | 'postponed' | 'cancelled' }>`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-line;
  ${props => {
    switch (props.$variant) {
      case 'handled':
        return `background: #F0FDF4; border: 1px solid rgba(22, 163, 74, 0.25); color: #166534;`;
      case 'postponed':
        return `background: #FFFBEB; border: 1px solid rgba(217, 119, 6, 0.25); color: #92400E;`;
      default:
        return `background: var(--background-color); border: 1px solid var(--border-color); color: var(--text-secondary);`;
    }
  }}
`;

const StatusBlockTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

interface ReminderCardProps {
  reminder: Reminder;
  onUpdate?: () => void;
}

const formatFullDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ReminderCard: React.FC<ReminderCardProps> = ({ reminder, onUpdate }) => {
  const navigate = useNavigate();
  const isPending = reminder.status === ReminderStatus.Pending;
  const [showHandleInput, setShowHandleInput] = useState(false);
  const [handledNotes, setHandledNotes] = useState('');
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMarkAsHandled = async () => {
    if (!handledNotes.trim() && showHandleInput) {
      setError('נא להזין הערות טיפול');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await reminderApi.handleReminder(reminder.id, { handledNotes: handledNotes.trim() });
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetAssignees = async (userIds: number[]) => {
    try {
      await reminderApi.setAssignees(reminder.id, userIds);
      toast.success('המשויכים עודכנו בהצלחה');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה בעדכון המשויכים'));
      throw err;
    }
  };

  const handlePostpone = async (newDate: string, reason?: string) => {
    setLoading(true);
    setError(null);

    try {
      await reminderApi.postponeReminder(reminder.id, { 
        newDueDate: newDate, 
        reason 
      });
      setShowPostponeModal(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEntityClick = () => {
    if (!reminder.entityType || !reminder.entityId) return;

    // Navigate to client management page with schedule-tasks section
    if (reminder.entityType === 2) { // Client
      navigate(`/clients/${reminder.entityId}/manage/schedule-tasks`);
    }
    // For cases (entityType === 3), also navigate to the client page
    else if (reminder.entityType === 3) { // Case
      navigate(`/clients/${reminder.entityId}/manage/schedule-tasks`);
    }
    // For leads, navigate to the lead management page with schedule-tasks section
    else if (reminder.entityType === 1) { // Lead
      navigate(`/leads/${reminder.entityId}/manage/schedule-tasks`);
    }
  };

  const getEntityLabel = () => {
    if (!reminder.entityType) return null;

    let IconComponent = Pin;
    const name = reminder.entityName || `#${reminder.entityId}`;

    switch (reminder.entityType) {
      case 1: IconComponent = User; break; // Lead
      case 2: IconComponent = Handshake; break; // Client
      case 3: IconComponent = Folder; break; // Case
      default: IconComponent = Pin; break;
    }

    return { IconComponent, name };
  };

  return (
    <>
      <Card isOverdue={reminder.isOverdue}>
        <CardHeader>
          <TitleSection>
            <Title>
              <TypeIcon>{getReminderTypeIconComponent(reminder.reminderType)}</TypeIcon>
              {reminder.title}
            </Title>
          </TitleSection>
        </CardHeader>
        
        {reminder.description && (() => {
          // Highlight the failure block (added by Cardcom webhook handler).
          // Format: "<base description>\n\n⚠ שגיאת סליקה: <message>"
          const failureMarker = '\n\n⚠ שגיאת סליקה:';
          const idx = reminder.description.indexOf(failureMarker);
          if (idx === -1) {
            return <Description>{reminder.description}</Description>;
          }
          const baseDesc = reminder.description.substring(0, idx);
          const failureMsg = reminder.description.substring(idx + 3).trim(); // strip "\n\n"
          return (
            <>
              {baseDesc && <Description>{baseDesc}</Description>}
              <FailureBlock>{failureMsg}</FailureBlock>
            </>
          );
        })()}
        
        <MetaInfo>
          <DateTime isOverdue={reminder.isOverdue}>
            <IconWrapper><Clock size={14} /></IconWrapper>
            {formatReminderDate(reminder.dueDateTime)}
          </DateTime>
          {getEntityLabel() && (
            <EntityInfo>
              <IconWrapper>
                {React.createElement(getEntityLabel()!.IconComponent, { size: 14 })}
              </IconWrapper>
              <EntityLink onClick={handleEntityClick}>
                {getEntityLabel()!.name}
              </EntityLink>
            </EntityInfo>
          )}
        </MetaInfo>

        <AssigneeRow>
          <AssigneesControl
            assignees={reminder.assignees}
            onSave={handleSetAssignees}
          />
        </AssigneeRow>

        {error && (
          <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '8px' }}>
            {error}
          </div>
        )}

        {/* תזכורת שטופלה — תצוגת קריאה-בלבד עם פרטי הטיפול */}
        {reminder.status === ReminderStatus.Handled && (
          <StatusBlock $variant="handled">
            <StatusBlockTitle>
              <Check size={13} style={{ verticalAlign: 'middle', marginLeft: '4px' }} />
              טופל{reminder.handledByName ? ` ע״י ${reminder.handledByName}` : ''}
              {reminder.handledAt ? ` · ${formatFullDate(reminder.handledAt)}` : ''}
            </StatusBlockTitle>
            {reminder.handledNotes && <div>הערות: {reminder.handledNotes}</div>}
          </StatusBlock>
        )}

        {/* תזכורת שנדחתה */}
        {reminder.status === ReminderStatus.Postponed && (
          <StatusBlock $variant="postponed">
            <StatusBlockTitle>
              <Timer size={13} style={{ verticalAlign: 'middle', marginLeft: '4px' }} />
              נדחתה
            </StatusBlockTitle>
          </StatusBlock>
        )}

        {/* תזכורת שבוטלה */}
        {reminder.status === ReminderStatus.Cancelled && (
          <StatusBlock $variant="cancelled">
            <StatusBlockTitle>בוטלה</StatusBlockTitle>
          </StatusBlock>
        )}

        {isPending && showHandleInput && (
          <HandledNotesInput
            placeholder="הערות טיפול..."
            value={handledNotes}
            onChange={(e) => setHandledNotes(e.target.value)}
            disabled={loading}
          />
        )}

        {isPending && (
          <Actions>
            {!showHandleInput ? (
              <>
                <TextButton
                  $color="var(--success-color)"
                  $outlined
                  onClick={() => setShowHandleInput(true)}
                  disabled={loading}
                >
                  <Check size={14} /> סמן כטופל
                </TextButton>
                <TextButton
                  $color="var(--text-secondary)"
                  onClick={() => setShowPostponeModal(true)}
                  disabled={loading}
                >
                  <Timer size={14} /> דחה
                </TextButton>
              </>
            ) : (
              <>
                <ActionButton
                  variant="primary"
                  onClick={handleMarkAsHandled}
                  disabled={loading}
                >
                  {loading ? 'שומר...' : 'שמור'}
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setShowHandleInput(false);
                    setHandledNotes('');
                    setError(null);
                  }}
                  disabled={loading}
                >
                  ביטול
                </ActionButton>
              </>
            )}
          </Actions>
        )}
      </Card>

      {showPostponeModal && (
        <PostponeReminderModal
          isOpen={showPostponeModal}
          reminder={reminder}
          onClose={() => setShowPostponeModal(false)}
          onPostpone={handlePostpone}
        />
      )}
    </>
  );
};

export default ReminderCard;